import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import {
  fetchFinanceControl,
  fetchOrderDecisions,
  fetchOrderReferenceData,
  fetchOrderReportFile,
} from './api';
import DateField from './DateField';
import OrderAccess from './OrderAccess';
import PageHeader from './PageHeader';
import SearchableSelect from './SearchableSelect';
import {
  FinanceControlRow,
  OrderHistoryRow,
  OrderReportFilters,
  OrderReportFormat,
  OrderReportKind,
  SelectOption,
} from './types';
import {
  getOrderEventLabel,
  isAutomaticAcceptance,
  isInvalidDate,
  validateReportPeriod,
} from './utils';
import * as Styles from './Orders.css';

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

const downloadFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const INITIAL_FILTERS: OrderReportFilters = {
  from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  to: moment().format('YYYY-MM-DD'),
};

const reportErrorMessage = (
  error: 'forbidden' | 'decisions' | 'finance' | 'all' | 'download',
) => {
  if (error === 'forbidden') {
    return 'ORDER_OVERSIGHT.UNAUTHORIZED';
  }
  if (error === 'download') {
    return 'ORDER_OVERSIGHT.DOWNLOAD_ERROR';
  }
  if (error === 'decisions' || error === 'finance') {
    return 'ORDER_OVERSIGHT.PARTIAL';
  }
  return 'ORDER_OVERSIGHT.ERROR';
};

export const OrderOversightContent = () => {
  const intl = useIntl();
  const [filters, setFilters] = useState<OrderReportFilters>(INITIAL_FILTERS);
  const [applied, setApplied] = useState<OrderReportFilters>(INITIAL_FILTERS);
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [referencePartial, setReferencePartial] = useState(false);
  const [decisions, setDecisions] = useState<OrderHistoryRow[]>([]);
  const [finance, setFinance] = useState<FinanceControlRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [download, setDownload] = useState('');
  const [validation, setValidation] = useState('');
  const [error, setError] = useState<
    'forbidden' | 'decisions' | 'finance' | 'all' | 'download' | null
  >(null);
  const latestRequest = useRef(0);

  useEffect(() => {
    let mounted = true;
    fetchOrderReferenceData().then((response) => {
      if (!mounted) {
        return;
      }
      setClients(response.clients);
      setEmployees(response.employees);
      setReferencePartial(!response.clientsOk || !response.employeesOk);
    });
    return () => {
      mounted = false;
      latestRequest.current += 1;
    };
  }, []);

  const load = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError(null);
    const [decisionResponse, financeResponse] = await Promise.all([
      fetchOrderDecisions(applied),
      fetchFinanceControl(applied),
    ]);
    if (requestId !== latestRequest.current) {
      return;
    }
    setLoading(false);
    if (decisionResponse.status === 403 || financeResponse.status === 403) {
      setDecisions([]);
      setFinance([]);
      setError('forbidden');
      return;
    }
    if (decisionResponse.ok && decisionResponse.data) {
      setDecisions(decisionResponse.data);
    } else {
      setDecisions([]);
    }
    if (financeResponse.ok && financeResponse.data) {
      setFinance(financeResponse.data);
    } else {
      setFinance([]);
    }
    if (!decisionResponse.ok && !financeResponse.ok) {
      setError('all');
    } else if (!decisionResponse.ok) {
      setError('decisions');
    } else if (!financeResponse.ok) {
      setError('finance');
    }
  }, [applied]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = () => {
    const message = validateReportPeriod(filters.from, filters.to);
    setValidation(message);
    if (!message) {
      setApplied(filters);
    }
  };

  const exportReport = async (
    kind: OrderReportKind,
    format: OrderReportFormat,
  ) => {
    const key = `${kind}-${format}`;
    setDownload(key);
    setError(null);
    const response = await fetchOrderReportFile(kind, format, applied);
    setDownload('');
    if (response.ok && response.data) {
      downloadFile(response.data.blob, response.data.filename);
    } else {
      setError(response.status === 403 ? 'forbidden' : 'download');
    }
  };

  const update = (key: keyof OrderReportFilters, value: string) => {
    setValidation('');
    const normalizedValue =
      (key === 'clientId' || key === 'consultantId') && value
        ? Number(value)
        : value || undefined;
    setFilters((current) => ({
      ...current,
      [key]: normalizedValue,
    }));
  };

  const errorMessage = error ? reportErrorMessage(error) : '';
  const showDecisionEmpty =
    !loading &&
    !decisions.length &&
    !['decisions', 'all', 'forbidden'].includes(error || '');
  const showFinanceEmpty =
    !loading &&
    !finance.length &&
    !['finance', 'all', 'forbidden'].includes(error || '');

  return (
    <Styles.Page>
      <PageHeader
        title={intl.formatMessage({ id: 'ORDER_OVERSIGHT.TITLE' })}
        description={intl.formatMessage({ id: 'ORDER_OVERSIGHT.DESCRIPTION' })}
      />

      {referencePartial && (
        <Alert severity="warning">
          {intl.formatMessage({ id: 'ORDER_OVERSIGHT.REFERENCE_PARTIAL' })}
        </Alert>
      )}
      {validation && (
        <Alert severity="warning">
          {intl.formatMessage({ id: validation })}
        </Alert>
      )}
      {errorMessage && (
        <Alert
          severity={
            error === 'decisions' || error === 'finance' ? 'warning' : 'error'
          }
          action={
            error !== 'forbidden' && error !== 'download' ? (
              <Button onClick={load}>
                {intl.formatMessage({ id: 'ORDERS.RETRY' })}
              </Button>
            ) : undefined
          }
        >
          {intl.formatMessage({ id: errorMessage })}
        </Alert>
      )}

      <Styles.Filters elevation={1}>
        <DateField
          id="order-oversight-from"
          size="small"
          label={intl.formatMessage({ id: 'ORDER_OVERSIGHT.FROM' })}
          value={filters.from}
          onChange={(value) => !isInvalidDate(value) && update('from', value)}
        />
        <DateField
          id="order-oversight-to"
          size="small"
          label={intl.formatMessage({ id: 'ORDER_OVERSIGHT.TO' })}
          value={filters.to}
          onChange={(value) => !isInvalidDate(value) && update('to', value)}
        />
        <SearchableSelect
          id="order-oversight-client"
          label={intl.formatMessage({ id: 'ORDERS.CLIENT' })}
          options={clients.map(({ value, name }) => ({ value, label: name }))}
          value={filters.clientId ? String(filters.clientId) : ''}
          onChange={(value) => update('clientId', value)}
        />
        <SearchableSelect
          id="order-oversight-consultant"
          label={intl.formatMessage({ id: 'ORDERS.CONSULTANT' })}
          options={employees.map(({ value, name }) => ({ value, label: name }))}
          value={filters.consultantId ? String(filters.consultantId) : ''}
          onChange={(value) => update('consultantId', value)}
          helperText={intl.formatMessage({
            id: 'ORDER_OVERSIGHT.CONSULTANT_HELP',
          })}
        />
        <Box alignSelf="flex-start">
          <Button color="primary" variant="contained" onClick={apply}>
            {intl.formatMessage({ id: 'ORDERS.FILTER.APPLY' })}
          </Button>
        </Box>
      </Styles.Filters>

      <Styles.Toolbar>
        <Typography variant="h5">
          {intl.formatMessage({ id: 'ORDER_OVERSIGHT.DECISIONS' })}
        </Typography>
        <Box display="flex" flexWrap="wrap" gridGap={8}>
          {(['csv', 'xlsx'] as OrderReportFormat[]).map((format) => (
            <Button
              key={`decisions-${format}`}
              variant="outlined"
              disabled={Boolean(download)}
              onClick={() => exportReport('decisions', format)}
            >
              {download === `decisions-${format}` ? (
                <CircularProgress size={18} />
              ) : (
                intl.formatMessage({
                  id: `ORDER_OVERSIGHT.EXPORT_${format.toUpperCase()}`,
                })
              )}
            </Button>
          ))}
          {(['csv', 'xlsx'] as OrderReportFormat[]).map((format) => (
            <Tooltip
              key={`history-${format}`}
              title={intl.formatMessage({ id: 'ORDER_OVERSIGHT.HISTORY_HELP' })}
            >
              <span>
                <Button
                  variant="outlined"
                  disabled={Boolean(download)}
                  onClick={() => exportReport('history', format)}
                >
                  {download === `history-${format}` ? (
                    <CircularProgress size={18} />
                  ) : (
                    intl.formatMessage({
                      id: `ORDER_OVERSIGHT.HISTORY_EXPORT_${format.toUpperCase()}`,
                    })
                  )}
                </Button>
              </span>
            </Tooltip>
          ))}
        </Box>
      </Styles.Toolbar>

      {loading && (
        <Box p={4} textAlign="center">
          <CircularProgress />
        </Box>
      )}
      {!loading && decisions.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  'NUMBER',
                  'CONSULTANT',
                  'CLIENT',
                  'EVENT',
                  'STATUS',
                  'ACTIVATION',
                  'EFFECTIVE_END',
                  'ACTOR',
                  'REASON',
                  'OCCURRED',
                ].map((column) => (
                  <TableCell key={column}>
                    {intl.formatMessage({ id: `ORDER_OVERSIGHT.${column}` })}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {decisions.map((row) => {
                const automatic = isAutomaticAcceptance(row, row.consultantId);
                return (
                  <TableRow
                    key={`${row.orderId}-${row.occurredAt}-${row.eventType}`}
                  >
                    <Styles.NoWrapCell>
                      <Link to={`/assignments/${row.orderNumber}`}>
                        {row.orderNumber}
                      </Link>
                    </Styles.NoWrapCell>
                    <TableCell>{row.consultantName}</TableCell>
                    <TableCell>{row.clientName}</TableCell>
                    <TableCell>
                      {intl.formatMessage({
                        id: getOrderEventLabel(row.eventType, automatic),
                      })}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({
                        id: automatic
                          ? 'ORDERS.STATUS.ISSUED'
                          : `ORDERS.STATUS.${row.resultingStatus}`,
                      })}
                    </TableCell>
                    <Styles.NoWrapCell>
                      {formatDate(row.activationDate)}
                    </Styles.NoWrapCell>
                    <Styles.NoWrapCell>
                      {formatDate(row.effectiveEndDate)}
                    </Styles.NoWrapCell>
                    <TableCell>
                      {row.actorName ||
                        intl.formatMessage({ id: 'ORDERS.SYSTEM' })}
                    </TableCell>
                    <TableCell>{(!automatic && row.reason) || '—'}</TableCell>
                    <Styles.NoWrapCell>
                      {moment(row.occurredAt).format('DD/MM/YYYY HH:mm')}
                    </Styles.NoWrapCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {showDecisionEmpty && (
        <Alert severity="info">
          {intl.formatMessage({ id: 'ORDER_OVERSIGHT.DECISIONS_EMPTY' })}
        </Alert>
      )}

      <Box mt={3}>
        <Typography variant="h5">
          {intl.formatMessage({ id: 'ORDER_OVERSIGHT.FINANCE' })}
        </Typography>
        {!loading && finance.length > 0 && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    'NUMBER',
                    'CONSULTANT',
                    'CLIENT',
                    'PERIOD',
                    'HOURS',
                    'DAYS',
                    'INVOICE',
                    'RECONCILIATION',
                  ].map((column) => (
                    <TableCell key={column}>
                      {intl.formatMessage({ id: `ORDER_OVERSIGHT.${column}` })}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {finance.map((row) => (
                  <TableRow key={row.orderId}>
                    <Styles.NoWrapCell>
                      <Link to={`/assignments/${row.orderNumber}`}>
                        {row.orderNumber}
                      </Link>
                    </Styles.NoWrapCell>
                    <TableCell>{row.consultantName}</TableCell>
                    <TableCell>{row.clientName}</TableCell>
                    <Styles.NoWrapCell>
                      {formatDate(row.orderStartDate)} –{' '}
                      {formatDate(row.orderEndDate)}
                    </Styles.NoWrapCell>
                    <TableCell>{row.orderHours}</TableCell>
                    <TableCell>{row.orderDays}</TableCell>
                    <TableCell>{row.invoiceNetValuePln}</TableCell>
                    <TableCell>{row.reconciliationStatus}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {showFinanceEmpty && (
          <Alert severity="info">
            {intl.formatMessage({ id: 'ORDER_OVERSIGHT.FINANCE_EMPTY' })}
          </Alert>
        )}
      </Box>
    </Styles.Page>
  );
};

const OrderOversight = () => (
  <OrderAccess>{() => <OrderOversightContent />}</OrderAccess>
);

export default OrderOversight;
